import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import './AdminDashboard.css';

interface GalleryImage {
  filename: string;
  url: string;
}

const AdminDashboard: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchImages();
  }, [navigate]);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/gallery', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        setError('Failed to fetch images');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append('images', files[i]);
      }

      const response = await fetch('/api/gallery/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setSuccess(`${files.length} image(s) uploaded successfully`);
        fetchImages();
        e.target.value = '';
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = (filename: string) => {
    setDeleteId(filename);
  };

  const cancelDelete = () => {
    setDeleteId(null);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    const filename = deleteId;
    setDeleteId(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/api/gallery/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Image deleted successfully');
        fetchImages();
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        setError('Failed to delete image');
      }
    } catch (err) {
      setError('Failed to delete image');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="loading">Töltés...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="admin-dashboard-content">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="upload-section">
          <h2>Kép feltöltés</h2>
          <div className="upload-area">
            <input
              type="file"
              id="file-upload"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label htmlFor="file-upload" className="upload-label">
              <span className="upload-icon">☁️</span>
              {uploading ? 'Feltöltés...' : 'Válasszon képet vagy használja a Drag & Drop beszúrást'}
            </label>
          </div>
        </div>

        <div className="gallery-section">
          <h2>Galléria képek ({images.length})</h2>
          <div className="image-grid">
            {images.map((image) => (
              <div key={image.filename} className="image-item">
                <img src={image.url} alt={image.filename} />
                <div className="image-overlay">
                  <button
                    onClick={() => confirmDelete(image.filename)}
                    className="delete-btn"
                  >
                    Törlés
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Törli a képet?</h3>
            <p>Biztos törli?</p>
            <div className="modal-actions">
              <button onClick={cancelDelete} className="modal-btn cancel">Mégse</button>
              <button onClick={handleDelete} className="modal-btn delete">Törlés</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
